import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { cn } from '@/shared/lib/cn';

import { activeNavItemStyle } from './DesktopSidebar.styles';
import { DirectoriesNavItem } from './DirectoriesNavItem';
import { useSidebarCollapsed } from './sidebarCollapse';
import { SidebarTooltip } from './SidebarTooltip';
import type {
  SidebarIcon,
  SidebarLinkItem,
  SidebarSection,
} from './DesktopSidebar.types';

export function NavChevron({ open }: { open: boolean }) {
  return (
    <ChevronDown
      className={cn(
        'ml-auto size-4 shrink-0 text-[#313846] transition-transform',
        open && 'rotate-180'
      )}
      strokeWidth={1.8}
    />
  );
}

export function SidebarItemContent({
  Icon,
  label,
  active = false,
  trailing,
}: {
  Icon: SidebarIcon;
  label: string;
  active?: boolean;
  trailing?: ReactNode;
}) {
  const collapsed = useSidebarCollapsed();

  // Свёрнутый сайдбар: только иконка, крупнее — она теперь сама кнопка.
  // Подпись остаётся для скринридеров, зрячим её показывает SidebarTooltip.
  // Нижняя граница у значка и строки — страховка к масштабу меню (не ниже
  // 0.8, useNavFitScale): иконке нужно больше, чем тексту. Не влезли —
  // список прокручивается (DesktopSidebarMenu).
  if (collapsed) {
    return (
      <>
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-[6px]',
            'size-[max(22px,calc(26px*var(--nav-scale,1)))]',
            active ? 'bg-white text-[#102653]' : 'text-[#4B5563]'
          )}
        >
          <Icon
            className="size-[max(14px,calc(17px*var(--nav-scale,1)))]"
            strokeWidth={1.7}
          />
        </span>
        <span className="sr-only">{label}</span>
      </>
    );
  }

  return (
    <>
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-[2.67px]',
          'size-[calc(16px*var(--nav-scale,1))]',
          active ? 'bg-white text-[#102653]' : 'text-[#4B5563]'
        )}
      >
        <Icon
          className="size-[calc(10.67px*var(--nav-scale,1))]"
          strokeWidth={1.7}
        />
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {trailing}
    </>
  );
}

export function getNavItemClass(
  active = false,
  soft = false,
  collapsed = false
) {
  return cn(
    collapsed
      ? [
          'mx-auto flex w-[44px] shrink-0 items-center justify-center rounded-[10px] transition-colors',
          'h-[max(26px,calc(32px*var(--nav-scale,1)))]',
        ]
      : [
          'flex w-full items-center rounded-[8px] py-[5px] pr-[10px] pl-[16px] text-left font-normal leading-[100%] tracking-[-0.03em] transition-colors',
          'h-[calc(30px*var(--nav-scale,1))] gap-[calc(11px*var(--nav-scale,1))] text-[calc(16px*var(--nav-scale,1))]',
        ],
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#102653]/20',
    active
      ? 'text-white hover:text-white'
      : 'text-[#4B5563] hover:bg-[#F6F7FA] hover:text-[#17244A]',
    soft && !active && 'bg-[#F7F8FA]',
    'disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-[#4B5563]'
  );
}

export function SidebarLink({ item }: { item: SidebarLinkItem }) {
  const collapsed = useSidebarCollapsed();

  if (item.kind === 'directories') {
    return <DirectoriesNavItem item={item} />;
  }

  if (item.to === '#') {
    return (
      <SidebarTooltip label={item.label}>
        <button
          type="button"
          className={getNavItemClass(false, false, collapsed)}
          aria-disabled="true"
        >
          <SidebarItemContent Icon={item.icon} label={item.label} />
        </button>
      </SidebarTooltip>
    );
  }

  // Триггер подсказки — обёртка, а не сам NavLink: Slot у Radix склеивает
  // className строкой, и функция-className ссылки (активный пункт) пришла бы
  // в DOM исходным текстом. Фокус ссылки всплывает до обёртки — подсказка
  // открывается и с клавиатуры.
  return (
    <SidebarTooltip label={item.label}>
      <div>
        <NavLink
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            getNavItemClass(isActive, false, collapsed)
          }
          style={({ isActive }) => (isActive ? activeNavItemStyle : undefined)}
        >
          {({ isActive }) => (
            <SidebarItemContent
              Icon={item.icon}
              label={item.label}
              active={isActive}
            />
          )}
        </NavLink>
      </div>
    </SidebarTooltip>
  );
}

export function SidebarAccordion({
  title,
  open,
  onToggle,
  children,
  divider = false,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
  /** Черта над секцией в свёрнутом сайдбаре — у всех, кроме первой. */
  divider?: boolean;
}) {
  const collapsed = useSidebarCollapsed();

  // Свёрнуто: заголовков-аккордеонов нет (подписи не влезают), секции
  // разделены чертой, пункты видны все — свернуть секцию тут нечем.
  if (collapsed) {
    return (
      <section aria-label={title}>
        {divider ? (
          <div
            aria-hidden="true"
            className="mx-auto my-[calc(5px*var(--nav-scale,1))] h-px w-7 bg-[#E7EAF0]"
          />
        ) : null}
        <div className="flex flex-col gap-[calc(2px*var(--nav-scale,1))] py-[calc(2px*var(--nav-scale,1))]">
          {children}
        </div>
      </section>
    );
  }

  return (
    <section>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={cn(
          'flex w-full items-center gap-2 rounded-[6px] px-2 text-left font-normal leading-none tracking-normal text-[#8A9099] transition-colors',
          'py-[calc(5px*var(--nav-scale,1))] text-[calc(11px*var(--nav-scale,1))]',
          'hover:bg-[#F6F7FA] hover:text-[#17244A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#102653]/20'
        )}
      >
        <span className="min-w-0 flex-1 truncate">{title}</span>
      </button>
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-[calc(2px*var(--nav-scale,1))] py-[calc(2px*var(--nav-scale,1))]">
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}

export function SidebarSectionBlock({
  section,
  open,
  onToggle,
  divider = false,
}: {
  section: SidebarSection;
  open: boolean;
  onToggle: () => void;
  divider?: boolean;
}) {
  return (
    <SidebarAccordion
      title={section.title}
      open={open}
      onToggle={onToggle}
      divider={divider}
    >
      {section.items.map((item) => (
        <SidebarLink key={item.label} item={item} />
      ))}
    </SidebarAccordion>
  );
}
